/**
 * Centralized Pricing Configuration
 * All prices, features, and tier metadata in one place.
 * Update this file to change pricing across the entire app.
 */

export type TierId = 'free' | 'day_pass' | 'pro_monthly' | 'pro_annual' | 'campus_monthly' | 'campus_annual' | 'district';
export type BillingPeriod = 'monthly' | 'annual';

export interface TierLimits {
  maxFileSizeMB: number;
  maxPdfPages: number;
  maxRedactionsPerDay: number;
  hasWatermark: boolean;
  batchUploadLimit: number;
  hasProcessingHistory: boolean;
  hasPriorityProcessing: boolean;
}

export interface TierPricing {
  id: TierId;
  name: string;
  monthlyPrice: number | null; // null for contact-us pricing
  annualPrice: number | null;
  period: string;
  tagline: string;
  features: string[];
  limits: TierLimits;
  badge?: string;
  isPopular?: boolean;
  isContactSales?: boolean;
  stripePriceId?: {
    monthly?: string;
    annual?: string;
  };
  /** Former monthly list price (display-only strikethrough) */
  listMonthlyPrice?: number;
}

// Stripe Price IDs
export const STRIPE_PRICE_IDS = {
  day_pass: 'price_1T2k9xEReu7slVc9tFzpoOu1',
  /** Pro monthly checkout */
  pro_monthly: 'price_1SbYXDEReu7slVc991a1hfXn',
  /** $14.99/mo — keep active in Stripe for manual rollback; not used at checkout during promo */
  pro_monthly_list_price: 'price_1TFmVOEReu7slVc9ShT1wnAQ',
  /** Pro Annual ($99/year) */
  pro_annual: 'price_1TFmVUEReu7slVc9tLINC5tj',
  campus_monthly: 'price_1TFmVrEReu7slVc9nT2vsvA5',
  campus_annual: 'price_1TFmW1EReu7slVc9hmH705nO',
};

// Tier Limits Configuration
export const TIER_LIMITS: Record<string, TierLimits> = {
  free: {
    maxFileSizeMB: 20,
    maxPdfPages: 3,
    maxRedactionsPerDay: 5,
    hasWatermark: true,
    batchUploadLimit: 1,
    hasProcessingHistory: false,
    hasPriorityProcessing: false,
  },
  day_pass: {
    maxFileSizeMB: 25,
    maxPdfPages: 50,
    maxRedactionsPerDay: Infinity,
    hasWatermark: false,
    batchUploadLimit: 1,
    hasProcessingHistory: false,
    hasPriorityProcessing: false,
  },
  pro: {
    maxFileSizeMB: 50,
    maxPdfPages: 100,
    maxRedactionsPerDay: Infinity,
    hasWatermark: false,
    batchUploadLimit: 5,
    hasProcessingHistory: true,
    hasPriorityProcessing: true,
  },
  campus: {
    maxFileSizeMB: 50,
    maxPdfPages: 100,
    maxRedactionsPerDay: Infinity,
    hasWatermark: false,
    batchUploadLimit: 5,
    hasProcessingHistory: true,
    hasPriorityProcessing: true,
  },
  district: {
    maxFileSizeMB: 50,
    maxPdfPages: 100,
    maxRedactionsPerDay: Infinity,
    hasWatermark: false,
    batchUploadLimit: 5,
    hasProcessingHistory: true,
    hasPriorityProcessing: true,
  },
};

// Full Tier Configuration
export const PRICING_TIERS: TierPricing[] = [
  {
    id: 'free',
    name: 'Free',
    monthlyPrice: 0,
    annualPrice: 0,
    period: '',
    tagline: 'For occasional / light document preparation',
    features: [
      '5 preparations per day',
      'Max 20 MB file size',
      'Max 3 pages per PDF',
      'Watermarked prepared output',
      'Standard processing',
    ],
    limits: TIER_LIMITS.free,
  },
  {
    id: 'day_pass',
    name: 'Day Pass',
    monthlyPrice: 4.99,
    annualPrice: null,
    period: '24 hours',
    tagline: 'Perfect for one-time projects',
    features: [
      'Unlimited preparations',
      'Up to 25 MB uploads',
      'Up to 50 pages per file',
      'No watermark',
      'Standard processing',
    ],
    limits: TIER_LIMITS.day_pass,
    stripePriceId: {
      monthly: STRIPE_PRICE_IDS.day_pass,
    },
  },
  {
    id: 'pro_monthly',
    name: 'Pro',
    monthlyPrice: 9.99,
    listMonthlyPrice: 14.99,
    annualPrice: 99,
    period: 'month',
    tagline: 'For educators regularly preparing documents for AI workflows',
    features: [
      '7-day free trial on monthly plans',
      'Unlimited preparations',
      'Up to 50 MB uploads',
      'Up to 100 pages per file',
      'No watermark',
      'Batch prepare (up to 5 files)',
      'Priority processing',
      'Cancel anytime',
    ],
    limits: TIER_LIMITS.pro,
    isPopular: true,
    stripePriceId: {
      monthly: STRIPE_PRICE_IDS.pro_monthly,
      annual: STRIPE_PRICE_IDS.pro_annual,
    },
  },
  {
    id: 'campus_monthly',
    name: 'Campus',
    monthlyPrice: null,
    annualPrice: null,
    period: '',
    tagline: 'For schools, SPED departments, evaluation teams, and university programs',
    features: [
      'Flexible access for education teams',
      'Higher-volume document preparation',
      'Support for educational workflows',
      'Pilot and implementation options',
      'Customized onboarding and support',
      'Volume-based options',
    ],
    limits: TIER_LIMITS.campus,
    badge: 'Best for teams',
    /** Milestone 0: sales-assisted — no org/seat product yet */
    isContactSales: true,
    stripePriceId: {
      monthly: STRIPE_PRICE_IDS.campus_monthly,
      annual: STRIPE_PRICE_IDS.campus_annual,
    },
  },
  {
    id: 'district',
    name: 'District',
    monthlyPrice: null,
    annualPrice: null,
    period: '',
    tagline: 'For districts exploring secure AI-enabled document workflows',
    features: [
      'District-level implementation options',
      'Custom usage and access needs',
      'AI preparation workflow consultation',
      'Professional development integration',
      'Custom support options',
      'Discuss privacy and security requirements',
    ],
    limits: TIER_LIMITS.district,
    isContactSales: true,
  },
];

// Helper functions
export function getTierById(id: TierId): TierPricing | undefined {
  return PRICING_TIERS.find(tier => tier.id === id || tier.id.startsWith(id.replace('_monthly', '').replace('_annual', '')));
}

export function getDisplayPrice(tier: TierPricing, billingPeriod: BillingPeriod): string {
  if (tier.isContactSales) return 'Contact us';
  if (tier.id === 'free') return '$0';
  if (tier.id === 'day_pass') return `$${tier.monthlyPrice}`;
  
  if (billingPeriod === 'annual' && tier.annualPrice) {
    return `$${tier.annualPrice}`;
  }
  return `$${tier.monthlyPrice}`;
}

export function getPeriodLabel(tier: TierPricing, billingPeriod: BillingPeriod): string {
  if (tier.isContactSales || tier.id === 'free') return '';
  if (tier.id === 'day_pass') return '/ 24 hours';
  
  if (billingPeriod === 'annual') {
    return '/ year';
  }
  return '/ month';
}

export function getSavingsLabel(tier: TierPricing, billingPeriod: BillingPeriod): string | null {
  if (tier.isContactSales) return null;
  if (billingPeriod !== 'annual' || !tier.monthlyPrice || !tier.annualPrice) return null;

  const monthlyTotal = tier.monthlyPrice * 12;
  if (tier.annualPrice >= monthlyTotal) return null;

  const savings = Math.round((1 - tier.annualPrice / monthlyTotal) * 100);
  if (savings <= 0) return null;

  return `Save ${savings}%`;
}

// Contact email for District tier
export const SALES_EMAIL = 'sales@thelearningindex.com';
